import instance from '@/api/instance';
import posthog from 'posthog-js';

/**
 * Utility composable - replaces global properties from main.js
 */
export function useUtils() {
  /**
   * API instance (replaces this.$api)
   */
  const api = instance;

  /**
   * PostHog capture (replaces this.$capture)
   */
  const capture = (event: string, props?: Record<string, any>) => {
    if (import.meta.env.VITE_ENV === 'development') {
      console.log(event, props);
      return;
    }
    posthog.capture(event, props);
  };

  /**
   * CDN URL (replaces this.$s3)
   */
  const s3 = 'https://cdn.strum.education';

  /**
   * Format date (replaces this.$formatDate)
   */
  const formatDate = (string: string | null | undefined, withMinutes = false) => {
    if (string === null || string === undefined) return '—';
    const date = new Date(string);
    if (date.toString() === 'Invalid Date') return '—';
    return date.toLocaleDateString('uk-UA', {
      year: '2-digit',
      month: 'numeric',
      day: 'numeric',
      ...(withMinutes && { minute: '2-digit', hour: '2-digit' })
    });
  };

  /**
   * Group array by key (replaces this.$groupBy)
   */
  const groupBy = (arrOfObjects: any[], key: string) => {
    if (!arrOfObjects?.length) return {};
    const obj = arrOfObjects.reduce((acc, obj, i) => {
      if (typeof obj !== 'object') return acc;
      const value = obj[key];
      if (!acc[value]) acc[value] = [];
      acc[value].push({ ...obj, index: i });
      return acc;
    }, {});
    return Object.entries(obj);
  };

  /**
   * Download file (replaces this.$download)
   */
  const download = (data: any, type: string, fileName: string) => {
    const blob = new Blob([data], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.download = fileName;
    a.href = url;
    a.click();
    URL.revokeObjectURL(url);
  };

  /**
   * Navigate to link (replaces this.$navigateLink)
   */
  const navigateLink = (url: string, options: { isBlank?: boolean; download?: string } = {}) => {
    const { isBlank, download: downloadName } = options;
    const a = document.createElement('a');
    a.href = url;
    if (isBlank) a.target = '_blank';
    if (downloadName) a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  /**
   * Map list for dropdowns (replaces this.$mapList)
   */
  const mapList = (arr: any[], params: Record<string, string | Function> = {}) => {
    const defaultParams = {
      value: '_id',
      label: 'name',
      info: 'description',
      icon: 'icon',
      color: 'color'
    };
    const mergedParams = { ...defaultParams, ...params };
    return arr?.map((e) =>
      Object.fromEntries(Object.entries(mergedParams).map(([k, v]) => [k, e[typeof v === 'function' ? v(e) : v]]))
    );
  };

  // Color constants (replaces this.$cmain, etc.)
  const colors = {
    cmain: '#140F2E',
    cdarkgray: '#666666',
    cmediumgray: '#c2c2c2',
    clightgray: '#f2f2f2',
    blue: '#74cfe3'
  };

  // Validation patterns (replaces this.$validationPassword, etc.)
  const validations = {
    password: {
      regexp: /^[A-Za-z0-9!#$%^*]+$/
    },
    login: {
      regexp: /^[a-zA-Z0-9_ ]*$/
    }
  };

  return {
    api,
    capture,
    s3,
    formatDate,
    groupBy,
    download,
    navigateLink,
    mapList,
    colors,
    validations
  };
}
